import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Animated, Dimensions, Easing } from 'react-native';

import styles from './lesson-activity-selector.style';
import { TemplateType } from '../../enums/template-type';
import { useActivityContext } from '../../hooks/use-activity.context';
import { ComprehensionChoose } from '../activities/comprehension-choose/comprehension-choose';
import { ComprehensionImage1 } from '../activities/comprehension-image-1/comprehension-image-1';
import { ComprehensionImage2 } from '../activities/comprehension-image-2/comprehension-image-2';
import { ComprehensionTrueFalse } from '../activities/comprehension-true-false/comprehension-true-false';
import { ComprehensionVideo } from '../activities/comprehension-video/comprehension-video';
import { DefinitionsChoose } from '../activities/definitions-choose/definitions-choose';
import { DefinitionsChooseAudio } from '../activities/definitions-choose-audio/definitions-choose-audio';
import { DefinitionsImage } from '../activities/definitions-image/definitions-image';
import { DefinitionsList } from '../activities/definitions-list/definitions-list';
import { DefinitionsMatch } from '../activities/definitions-match/definitions-match';
import { DefinitionsOrder } from '../activities/definitions-order/definitions-order';
import { GenericActivity } from '../activities/generic-activity/generic-activity';
import { DialogActivity } from '../activities/lesson/dialog-activity/dialog-activity';
import DibActivity from '../activities/lesson/dib-activity/dib-activity';
import { DNDActivity } from '../activities/lesson/dnd-activity/dnd-activity';
import { FourSquareActivity } from '../activities/lesson/four-square-activity/four-square-activity';
import { LearningActivity } from '../activities/lesson/learning-activity/learning-activity';
import { MultipleChoiceActivity } from '../activities/lesson/mc-activity/mc-activity';
import TabActivity from '../activities/lesson/tab-activity/tab-activity';
import { TitleActivity } from '../activities/lesson/title-activity/title-activity';
import { UnscrambleActivity } from '../activities/lesson/unscramble-activity/unscramble-activity';
import { NotImplementedActivity } from '../activities/not-implemented-activity/not-implemented-activity';
import { Pronunciation } from '../activities/pronunciation/pronunciation';
import { Reading } from '../activities/reading/reading';
import { ReadingListening } from '../activities/reading-listening/reading-listening';
import { SectionComplete } from '../activities/section-complete/section-complete.component';
import { SpellingAudio } from '../activities/spelling-audio/spelling-audio';
import { SpellingImage } from '../activities/spelling-image/spelling-image';
import { SpellingWriting } from '../activities/spelling-writing/spelling-writing';
import { VocabularyComplete } from '../activities/vocabulary-complete/vocabulary-complete';
import { VocabularyRead } from '../activities/vocabulary-read/vocabulary-read';
import { VocabularyVideo } from '../activities/vocabulary-video/vocabulary-video';

interface LessonActivitySelectorProps {
  activity: any;
  completionButtonText: string;
  completeActivity: () => void;
  updateStepProgress: (progress: number) => void;
  goNext: () => void;
  isComplete: boolean;
  isLast: boolean;
}

export function LessonActivitySelector(props: LessonActivitySelectorProps) {
  const visibility = useRef(new Animated.Value(1));
  const {
    currentActivity: activity,
    activityVisibility,
    goNextActivity,
    nextMovement,
    goPrevActivity,
  } = useActivityContext();
  const isMovingBack = nextMovement === 'left';

  const visibilityAnimation = useCallback(
    (value) =>
      Animated.timing(visibility.current, {
        toValue: value,
        duration: 300,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    [visibility]
  );

  useEffect(() => {
    if (activityVisibility) {
      return;
    }

    if (isMovingBack) {
      visibilityAnimation(1).start(() => goPrevActivity());
    } else {
      if (props.isLast) {
        goNextActivity();
      } else {
        visibilityAnimation(-1).start(() => goNextActivity());
      }
    }
  }, [
    activityVisibility,
    visibility,
    goNextActivity,
    props.isLast,
    visibilityAnimation,
    isMovingBack,
    goPrevActivity,
  ]);

  useEffect(() => {
    visibility.current.setValue(isMovingBack ? -1 : 1);
    visibilityAnimation(0).start();
  }, [activity, visibilityAnimation]);

  const currentActivity = useMemo(() => {
    let Activity;

    switch (props.activity.type) {
      case 'generic':
        Activity = GenericActivity;
        break;
      case 'VocabularyRead':
        Activity = VocabularyRead;
        break;
      case 'Reading':
        Activity = Reading;
        break;
      case 'ReadingListening':
        Activity = ReadingListening;
        break;
      case 'ComprehensionTrueFalse':
        Activity = ComprehensionTrueFalse;
        break;
      case 'SpellingWriting':
        Activity = SpellingWriting;
        break;
      case 'SpellingAudio':
        Activity = SpellingAudio;
        break;
      case 'DefinitionsChoose':
        Activity = DefinitionsChoose;
        break;
      case 'DefinitionsChooseAudio':
        Activity = DefinitionsChooseAudio;
        break;
      case 'ComprehensionChoose':
        Activity = ComprehensionChoose;
        break;
      case 'DefinitionsMatch':
        Activity = DefinitionsMatch;
        break;
      case 'Pronunciation':
        Activity = Pronunciation;
        break;
      case 'DefinitionsList':
        Activity = DefinitionsList;
        break;
      case 'DefinitionsOrder':
        Activity = DefinitionsOrder;
        break;
      case 'VocabularyComplete':
        Activity = VocabularyComplete;
        break;
      case 'SectionComplete':
        Activity = SectionComplete;
        break;
      case 'SpellingImage':
        Activity = SpellingImage;
        break;
      case 'ComprehensionImage1':
        Activity = ComprehensionImage1;
        break;
      case 'DefinitionsImage':
        Activity = DefinitionsImage;
        break;
      case 'ComprehensionImage2':
        Activity = ComprehensionImage2;
        break;
      case 'ComprehensionVideo':
        Activity = ComprehensionVideo;
        break;
      case 'VocabularyVideo':
        Activity = VocabularyVideo;
        break;
      case TemplateType.TITLE:
      case TemplateType.TITLE_EXTENDED:
        Activity = TitleActivity;
        break;
      case TemplateType.DIB:
      case TemplateType.DIB_EXTENDED:
      case TemplateType.TIB:
      case TemplateType.TIB_EXTENDED:
        Activity = DibActivity;
        break;
      case TemplateType.FOURSQ:
      case TemplateType.FOURSQ_EXTENDED:
        Activity = FourSquareActivity;
        break;
      case TemplateType.LEARNING:
      case TemplateType.LEARNING_EXTENDED:
        Activity = LearningActivity;
        break;
      case TemplateType.DIALOG:
      case TemplateType.DIALOG_EXTENDED:
        Activity = DialogActivity;
        break;
      case TemplateType.MC:
      case TemplateType.MC_EXTENDED:
        Activity = MultipleChoiceActivity;
        break;
      case TemplateType.UNSCRAMBLE:
      case TemplateType.UNSCRAMBLE_EXTENDED:
        Activity = UnscrambleActivity;
        break;
      case TemplateType.DND:
      case TemplateType.DND_EXTENDED:
        Activity = DNDActivity;
        break;
      case TemplateType.TAB:
      case TemplateType.TAB_EXTENDED:
        Activity = TabActivity;
        break;
      default:
        Activity = NotImplementedActivity;
    }

    return (
      <Activity
        {...props}
        goNext={goNextActivity}
        key={props.activity?.elementId}
      />
    );
  }, [props, goNextActivity]);

  const windowWidth = Dimensions.get('screen').width;
  const translateX = visibility.current.interpolate({
    inputRange: [-1, 0, 1],
    outputRange: [-windowWidth, 0, windowWidth],
  });

  const opacity = visibility.current.interpolate({
    inputRange: [-1, 0, 1],
    outputRange: [0.5, 1, 0.5],
  });

  return (
    <Animated.View
      style={[styles.container, { transform: [{ translateX }], opacity }]}
    >
      {currentActivity}
    </Animated.View>
  );
}
